/**
 * Client-side YouTube URL detection.
 *
 * This mirrors the backend validation rules so the UI can react the instant a
 * link is pasted. It is *feedback only* — the server always re-validates, and
 * the server's answer always wins.
 *
 * Accepted forms (per the design doc):
 *   https://www.youtube.com/watch?v=<id>
 *   https://m.youtube.com/watch?v=<id>
 *   https://music.youtube.com/watch?v=<id>
 *   https://youtu.be/<id>
 *   https://www.youtube.com/shorts/<id>
 *   https://www.youtube.com/embed/<id>
 *   https://www.youtube.com/live/<id>
 *   https://www.youtube.com/v/<id>
 *   https://www.youtube-nocookie.com/embed/<id>
 *
 * A URL carrying both `v=` and `list=` resolves to the single video (the
 * backend runs yt-dlp with `noplaylist: True`). A playlist-only URL is
 * rejected with PLAYLIST_ONLY.
 */

/** Hostnames we accept, already stripped of a leading `www.`. */
const ALLOWED_HOSTS: ReadonlySet<string> = new Set([
  'youtube.com',
  'm.youtube.com',
  'music.youtube.com',
  'youtube-nocookie.com',
  'youtu.be',
])

/** Path prefixes on youtube.com that carry the video id as the next segment. */
const ID_BEARING_PREFIXES: ReadonlySet<string> = new Set(['shorts', 'embed', 'live', 'v'])

/** YouTube video ids are exactly 11 URL-safe base64 characters. */
const VIDEO_ID_RE = /^[A-Za-z0-9_-]{11}$/

/** The subset of `ErrorCode` the client can determine on its own. */
export type YouTubeUrlFailure = 'NOT_YOUTUBE' | 'PLAYLIST_ONLY'

export type YouTubeUrlResult =
  | { ok: true; videoId: string; normalizedUrl: string }
  | { ok: false; code: YouTubeUrlFailure }

/** True when `value` is a syntactically valid YouTube video id. */
export function isVideoId(value: string): boolean {
  return VIDEO_ID_RE.test(value)
}

/**
 * Parse arbitrary user input into a video id.
 *
 * Never throws. A bare `youtu.be/<id>` without a scheme is accepted, because
 * that is what people paste from mobile share sheets.
 */
export function parseYouTubeUrl(input: string): YouTubeUrlResult {
  const raw = input.trim()
  if (raw === '') return { ok: false, code: 'NOT_YOUTUBE' }

  // Reject anything with whitespace inside — it is not a single URL.
  if (/\s/.test(raw)) return { ok: false, code: 'NOT_YOUTUBE' }

  const candidate = hasScheme(raw) ? raw : `https://${raw}`

  let url: URL
  try {
    url = new URL(candidate)
  } catch {
    return { ok: false, code: 'NOT_YOUTUBE' }
  }

  if (url.protocol !== 'http:' && url.protocol !== 'https:') {
    return { ok: false, code: 'NOT_YOUTUBE' }
  }

  const host = stripWww(url.hostname.toLowerCase())
  if (!ALLOWED_HOSTS.has(host)) return { ok: false, code: 'NOT_YOUTUBE' }

  const segments = url.pathname.split('/').filter((segment) => segment !== '')
  const first = segments[0] ?? ''
  const listParam = url.searchParams.get('list')
  const videoParam = url.searchParams.get('v')

  // youtu.be/<id> — the whole path is the id.
  if (host === 'youtu.be') {
    if (segments.length === 1 && isVideoId(first)) return success(first)
    // youtu.be links can also carry ?list= for a playlist share.
    if (segments.length === 0 && listParam) return { ok: false, code: 'PLAYLIST_ONLY' }
    return { ok: false, code: 'NOT_YOUTUBE' }
  }

  // Explicit playlist pages.
  if (first === 'playlist') return { ok: false, code: 'PLAYLIST_ONLY' }

  // /watch?v=<id>  (with or without &list=)
  if (first === 'watch') {
    if (videoParam && isVideoId(videoParam)) return success(videoParam)
    if (listParam) return { ok: false, code: 'PLAYLIST_ONLY' }
    return { ok: false, code: 'NOT_YOUTUBE' }
  }

  // /shorts/<id>, /embed/<id>, /live/<id>, /v/<id>
  if (ID_BEARING_PREFIXES.has(first)) {
    const id = segments[1] ?? ''
    if (isVideoId(id)) return success(id)
    return { ok: false, code: 'NOT_YOUTUBE' }
  }

  // Bare `?v=` with no path, e.g. https://www.youtube.com/?v=<id>
  if (segments.length === 0) {
    if (videoParam && isVideoId(videoParam)) return success(videoParam)
    if (listParam) return { ok: false, code: 'PLAYLIST_ONLY' }
  }

  return { ok: false, code: 'NOT_YOUTUBE' }
}

/** Convenience predicate: does this input point at a single YouTube video? */
export function isYouTubeUrl(input: string): boolean {
  return parseYouTubeUrl(input).ok
}

/** The video id, or `null` when the input is not a single-video YouTube URL. */
export function extractVideoId(input: string): string | null {
  const result = parseYouTubeUrl(input)
  return result.ok ? result.videoId : null
}

/**
 * Heuristic: does this text look like the user is *trying* to give us a link?
 *
 * Used to decide whether to show an inline "that's not a YouTube link" hint or
 * to stay quiet while someone is still typing.
 */
export function looksLikeUrlAttempt(input: string): boolean {
  const raw = input.trim()
  if (raw.length < 6) return false
  return raw.includes('://') || /\.[a-z]{2,}/i.test(raw)
}

function success(videoId: string): YouTubeUrlResult {
  return { ok: true, videoId, normalizedUrl: `https://www.youtube.com/watch?v=${videoId}` }
}

function hasScheme(value: string): boolean {
  return /^[a-z][a-z0-9+.-]*:/i.test(value)
}

function stripWww(host: string): string {
  return host.startsWith('www.') ? host.slice(4) : host
}
